/**
 * ATL-1445 — what this device saved from an Atlas API v1 draft, until the backend has recorded it.
 *
 * The sale or acceptance is saved by the app's own writer (Firestore first, then the dual-write);
 * reporting it back to `/api-drafts/:id/applied` is a separate request that can fail — offline, a
 * 5xx, a refusal. The support product drafts (ATL-1323) treat that report as best-effort, which is
 * right for a product: the worst case is a duplicate the card warns about. It is NOT right for a
 * sale: a draft that still reads "open" after its sale was saved invites a second tap, and a second
 * sale is a real sale — stock and cash counted twice.
 *
 * So the device writes down "draft D was saved as record R" BEFORE reporting, and forgets it only
 * when the backend has decided the draft. Until then the inbox shows that draft as saved on this
 * device (never as appliable) and retries the report. A report the backend refuses is kept with the
 * reason, so the merchant is told the record exists and why it was not linked, and can dismiss the
 * draft — never apply it again.
 *
 * Storage failures are not silent: a write that fails is thrown to the caller (the report still goes
 * out), a read that fails is reported as unknown rather than as "nothing pending".
 */
import AsyncStorage from '@react-native-async-storage/async-storage'
import { apiDraftsApi, type ApiDraft, type ApiDraftKind, type ApiDraftsApi } from './apiDraftsApi'
import { apiTokensErrorCode } from './apiTokensApi'

// v2: entries carry their organization. v1 never shipped in a release, so nothing is migrated.
export const DRAFT_REPORTS_STORAGE_KEY = 'atlas.apiDraftReports.v2'
/** Drafts expire after 7 days; a record is kept a week past that, then forgotten. */
export const DRAFT_REPORT_RETENTION_MS = 14 * 24 * 60 * 60 * 1000

export interface DraftReport {
  /**
   * The organization (Firestore id) the draft belongs to. A device can switch organizations; a
   * report retried under another one would read as "no such draft" and be stored as a refusal.
   */
  organizationId: string
  kind: ApiDraftKind
  firestoreId: string
  savedAt: string
  /** Set once the backend refused the record: it exists, but it is not linked to the draft. */
  refusedCode?: string
}

export type DraftReports = Record<string, DraftReport>

export interface KeyValueStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
}

export type ReportOutcome =
  | { outcome: 'recorded'; draft: ApiDraft }
  /** Someone already decided this draft (applied elsewhere or dismissed). Nothing to retry. */
  | { outcome: 'already_decided' }
  /** Worth retrying: offline, the backend or Firestore unavailable, the record not visible yet. */
  | { outcome: 'retry' }
  | { outcome: 'refused'; code: string }

const isReport = (r: unknown): r is DraftReport =>
  !!r &&
  typeof (r as DraftReport).organizationId === 'string' &&
  ((r as DraftReport).kind === 'sale' || (r as DraftReport).kind === 'acceptance') &&
  typeof (r as DraftReport).firestoreId === 'string' &&
  typeof (r as DraftReport).savedAt === 'string'

/** Parse stored reports, dropping malformed entries and those past retention. */
export function parseDraftReports(raw: string | null, now: number): DraftReports {
  if (!raw) return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const out: DraftReports = {}
  for (const [draftId, report] of Object.entries(parsed as Record<string, unknown>)) {
    if (!isReport(report)) continue
    const savedAt = Date.parse(report.savedAt)
    if (!Number.isFinite(savedAt) || now - savedAt > DRAFT_REPORT_RETENTION_MS) continue
    out[draftId] = report
  }
  return out
}

/** Classify a failed report. Exported for the tests; the rules are the point of this module. */
export function classifyReportError(error: unknown): ReportOutcome {
  const status = (error as { response?: { status?: unknown } })?.response?.status
  const code = apiTokensErrorCode(error)
  if (typeof status !== 'number') return { outcome: 'retry' } // no response: offline / timeout
  if (status === 409 && code === 'already_decided') return { outcome: 'already_decided' }
  // 401 and 403 are about the CALLER — a session not ready yet, or a person on this device whose
  // role cannot act on that kind (a cashier retrying an acceptance a manager saved here) — never
  // about the record, so they must not be stored as the record's refusal. The person who can will
  // retry it.
  if (status >= 500 || status === 401 || status === 403 || status === 408 || status === 429) {
    return { outcome: 'retry' }
  }
  // Firestore is strongly consistent, so a record the app has just committed should be visible;
  // if it is not, retrying is cheap and refusing would tell the merchant their sale is missing.
  if (code !== null && /_not_found$/.test(code)) return { outcome: 'retry' }
  return { outcome: 'refused', code: code ?? `http_${status}` }
}

export function createDraftReports(storage: KeyValueStorage, api: Pick<ApiDraftsApi, 'reportApplied'>) {
  const read = async (now: number): Promise<DraftReports> =>
    parseDraftReports(await storage.getItem(DRAFT_REPORTS_STORAGE_KEY), now)
  const write = (reports: DraftReports): Promise<void> =>
    storage.setItem(DRAFT_REPORTS_STORAGE_KEY, JSON.stringify(reports))

  // One chain so two reports (the screen's retry and a fresh save) never lose each other's write.
  let queue: Promise<unknown> = Promise.resolve()
  const serialized = <T>(fn: () => Promise<T>): Promise<T> => {
    const next = queue.then(fn, fn)
    queue = next.catch(() => undefined)
    return next
  }

  const update = (draftId: string, change: (current: DraftReport | undefined) => DraftReport | null) =>
    serialized(async () => {
      const reports = await read(Date.now())
      const next = change(reports[draftId])
      if (next === null) delete reports[draftId]
      else reports[draftId] = next
      await write(reports)
    })

  const report = async (draftId: string, firestoreId: string): Promise<ReportOutcome> => {
    let result: ReportOutcome
    try {
      result = { outcome: 'recorded', draft: await api.reportApplied(draftId, firestoreId) }
    } catch (error) {
      result = classifyReportError(error)
    }
    if (result.outcome === 'recorded' || result.outcome === 'already_decided') {
      await update(draftId, () => null)
    } else if (result.outcome === 'refused') {
      const code = result.code
      await update(draftId, (current) => (current ? { ...current, refusedCode: code } : null))
    }
    return result
  }

  return {
    /** What this device saved in this organization and the backend has not yet decided. */
    pending: (organizationId: string, now: number = Date.now()): Promise<DraftReports> =>
      serialized(async () =>
        Object.fromEntries(
          Object.entries(await read(now)).filter(([, entry]) => entry.organizationId === organizationId)
        )
      ),

    /**
     * Note the saved record, then report it. The note is written first: if the app dies between
     * the save and the report, the inbox still knows this draft is spent.
     */
    async saved(
      draft: { id: string; kind: ApiDraftKind; organizationId: string },
      firestoreId: string,
      now: number = Date.now()
    ): Promise<ReportOutcome> {
      const draftId = draft.id
      await update(draftId, () => ({
        organizationId: draft.organizationId,
        kind: draft.kind,
        firestoreId,
        savedAt: new Date(now).toISOString(),
      }))
      return report(draftId, firestoreId)
    },

    /**
     * Retry every report still waiting (not the refused ones: those need the merchant). `kinds`
     * limits it to the drafts the caller's screen implies a permission for: the till's cashier may
     * lack `Supplies`, and every retry of an acceptance someone else saved here would be a wasted
     * 403 (classified as retry, so the record is never marked refused by it).
     */
    async retryPending(
      organizationId: string,
      options: { now?: number; kinds?: readonly ApiDraftKind[] } = {}
    ): Promise<void> {
      const { now = Date.now(), kinds } = options
      const reports = await serialized(() => read(now))
      for (const [draftId, entry] of Object.entries(reports)) {
        if (entry.organizationId !== organizationId) continue
        if (kinds !== undefined && !kinds.includes(entry.kind)) continue
        if (entry.refusedCode === undefined) await report(draftId, entry.firestoreId)
      }
    },

    /** The merchant dismissed a draft whose record was refused: nothing left to track. */
    forget: (draftId: string): Promise<void> => update(draftId, () => null),
  }
}

export type DraftReportsStore = ReturnType<typeof createDraftReports>

/** This device's store, bound to AsyncStorage and the authenticated API client. */
export const deviceDraftReports: DraftReportsStore = createDraftReports(AsyncStorage, apiDraftsApi)
